import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, Check, ChevronLeft, Search, X } from 'lucide-react';
import classNames from 'classnames';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'shared/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from 'shared/components/ui/table';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import guidedPortfoliosStore, { resolvePlan } from 'shared/store/guidedPortfoliosStore';
import { ModelPortfoliosService, ModelPortfolioFamily } from 'shared/services/ModelPortfoliosService';
import { formatItalianNumber } from 'utils/formatUtils';

import styles from './GuidedPortfoliosDialog.module.scss';

type Props = {
  onImport: (text: string) => void;
  /** Current chat thread; the remembered plan is scoped to it (undefined = no thread yet). */
  threadId?: string;
};

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; families: ModelPortfolioFamily[] };

// Same buckets, order and colours as the diagnosis artifact's "Distribuzione per
// macro asset class" (PortfolioTablePie, paletteBarChart1..4).
const ALLOC_KEYS = ['azionario', 'obbligazionario', 'monetario', 'altro'] as const;
const ALLOC_COLORS = { azionario: '#103976', obbligazionario: '#228459', monetario: '#5CA8E6', altro: '#009688' } as const;
const ALLOC_LABELS = {
  azionario: 'guidedPortfolios.equity',
  obbligazionario: 'guidedPortfolios.bonds',
  monetario: 'guidedPortfolios.moneyMarket',
  altro: 'guidedPortfolios.other',
} as const;

export const GuidedPortfoliosDialog: React.FC<Props> = ({ onImport, threadId }) => {
  const { t } = useTranslation();
  const open = guidedPortfoliosStore((s) => s.open);
  const setOpen = guidedPortfoliosStore((s) => s.setOpen);
  const rememberedPlan = guidedPortfoliosStore((s) => s.rememberedPlan);
  const setRememberedPlan = guidedPortfoliosStore((s) => s.setRememberedPlan);
  const syncConversation = guidedPortfoliosStore((s) => s.syncConversation);

  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [query, setQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const fetchCatalogue = useCallback(() => {
    setLoad({ status: 'loading' });
    ModelPortfoliosService.getAll()
      .then(({ families }) => {
        setLoad({ status: 'ready', families });
        setSelectedType((current) =>
          families.some((f) => f.portfolio_type === current) ? current : (families[0]?.portfolio_type ?? null),
        );
      })
      .catch(() => setLoad({ status: 'error' }));
  }, []);

  // Every open starts from the first portfolio (DESIGN_SPEC "Fresh" state); only the
  // plan is remembered, and only within the current conversation.
  useEffect(() => {
    if (!open) return;
    syncConversation(threadId);
    setQuery('');
    setShowDetail(false);
    setSelectedType(null);
    fetchCatalogue();
  }, [open, threadId, syncConversation, fetchCatalogue]);

  const families = useMemo(() => (load.status === 'ready' ? load.families : []), [load]);
  const visible = useMemo(
    () => families.filter((f) => f.portfolio_type.toLowerCase().includes(query.trim().toLowerCase())),
    [families, query],
  );
  const family = families.find((f) => f.portfolio_type === selectedType) ?? null;
  const availablePlans = family ? family.plans.map((p) => p.financial_plan) : [];
  const { plan: shownPlan, overridden } = resolvePlan(rememberedPlan, availablePlans);
  const detail = family?.plans.find((p) => p.financial_plan === shownPlan) ?? null;
  const canImport = detail !== null;

  const selectFamily = (type: string) => {
    setSelectedType(type);
    setShowDetail(true);
  };

  const importSelection = () => {
    if (!detail) return;
    setOpen(false);
    onImport(t('guidedPortfolios.analyzeMessage', { portfolioName: detail.portfolio_name, code: detail.code }));
  };

  // Keyboard contract (DESIGN_SPEC): ↑/↓ move through the list, 1–8 pick a plan,
  // Enter imports when enabled (outside the search input and buttons), Esc is handled by Radix.
  const onKeyDown = (e: React.KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (visible.length === 0) return;
      e.preventDefault();
      const idx = visible.findIndex((f) => f.portfolio_type === selectedType);
      const next = e.key === 'ArrowDown' ? Math.min(idx + 1, visible.length - 1) : Math.max(idx - 1, 0);
      selectFamily(visible[next].portfolio_type);
      return;
    }
    if (/^[1-8]$/.test(e.key) && tag !== 'INPUT') {
      const plan = Number(e.key);
      if (availablePlans.includes(plan)) setRememberedPlan(plan);
      return;
    }
    if (e.key === 'Enter' && tag !== 'BUTTON' && tag !== 'INPUT' && canImport) {
      e.preventDefault();
      importSelection();
    }
  };

  const subtitle = !shownPlan
    ? t('guidedPortfolios.choosePlanSubtitle')
    : overridden
      ? t('guidedPortfolios.planFallback', { wanted: rememberedPlan, plan: shownPlan })
      : t('guidedPortfolios.planSubtitle', { plan: shownPlan, count: detail?.instruments.length ?? 0 });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className={styles.dialog}
        bodyClassName={styles.body}
        showHeader={false}
        onKeyDown={onKeyDown}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchRef.current?.focus();
        }}
      >
        <div className={styles.header}>
          <DialogTitle className={styles.title}>{t('guidedPortfolios.title')}</DialogTitle>
          <DialogDescription className={styles.subtitle}>{t('guidedPortfolios.subtitle')}</DialogDescription>
        </div>

        <div className={classNames(styles.panes, { [styles.showDetail]: showDetail })}>
          <div className={styles.left}>
            <div className={styles.search}>
              <Search size={18} />
              <input
                ref={searchRef}
                type="text"
                placeholder={t('guidedPortfolios.searchPlaceholder')}
                aria-label={t('guidedPortfolios.searchAria')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {query && (
                <button type="button" aria-label={t('guidedPortfolios.clearSearch')} onClick={() => setQuery('')}>
                  <X size={14} />
                </button>
              )}
            </div>

            <div className={styles.list}>
              {load.status === 'loading' && (
                <div className={styles.skeleton} aria-busy="true">
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
              )}
              {load.status === 'error' && (
                <div className={styles.placeholder}>
                  <strong>{t('guidedPortfolios.loadError')}</strong>
                  <FinecoButton copy={t('guidedPortfolios.retry')} click={fetchCatalogue} />
                </div>
              )}
              {load.status === 'ready' && families.length === 0 && (
                <p className={styles.muted}>{t('guidedPortfolios.empty')}</p>
              )}
              {load.status === 'ready' && families.length > 0 && visible.length === 0 && (
                <p className={styles.muted}>{t('guidedPortfolios.noResults')}</p>
              )}
              {visible.map((f) => {
                const plans = f.plans.map((p) => p.financial_plan);
                const active = f.portfolio_type === selectedType;
                return (
                  <button
                    key={f.portfolio_type}
                    type="button"
                    className={styles.item}
                    aria-pressed={active}
                    onClick={() => selectFamily(f.portfolio_type)}
                  >
                    <span>
                      <strong>{f.portfolio_type}</strong>
                      <small>{t('guidedPortfolios.plansCount', { count: plans.length })}</small>
                    </span>
                    {active && <Check size={20} strokeWidth={2.5} />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.right}>
            {!family ? (
              <div className={styles.placeholder}>
                <Briefcase size={40} color="#C4C7CC" />
                <strong>{t('guidedPortfolios.noSelectionTitle')}</strong>
                <span>{t('guidedPortfolios.noSelectionBody')}</span>
              </div>
            ) : (
              <>
                <div className={styles.detailHeader}>
                  <button
                    type="button"
                    className={classNames(styles.iconButton, styles.back)}
                    aria-label={t('guidedPortfolios.back')}
                    onClick={() => setShowDetail(false)}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className={styles.detailTitle}>
                    <strong>{detail ? detail.portfolio_name : family.portfolio_type}</strong>
                    <span>{subtitle}</span>
                  </div>
                  {detail && (
                    <div className={styles.alloc}>
                      <div className={styles.allocBar}>
                        {ALLOC_KEYS.map((k) => (
                          <div
                            key={k}
                            style={{ background: ALLOC_COLORS[k], width: `${Math.max(0, detail.allocation[k])}%` }}
                          />
                        ))}
                      </div>
                      <div className={styles.allocLegend}>
                        {ALLOC_KEYS.map((k) => (
                          <span key={k}>
                            <i style={{ background: ALLOC_COLORS[k] }} />
                            {t(ALLOC_LABELS[k])} {formatItalianNumber(detail.allocation[k], 2)}%
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={t('guidedPortfolios.deselect')}
                    onClick={() => {
                      setSelectedType(null);
                      setShowDetail(false);
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className={styles.plans}>
                  <span>{t('guidedPortfolios.planLabel')}</span>
                  {availablePlans.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={styles.chip}
                      aria-pressed={p === shownPlan}
                      onClick={() => setRememberedPlan(p)}
                    >
                      {`P${p}`}
                    </button>
                  ))}
                </div>

                {!detail ? (
                  <div className={classNames(styles.placeholder, styles.placeholderDashed)}>
                    <strong>{t('guidedPortfolios.choosePlanTitle')}</strong>
                    <span>{t('guidedPortfolios.choosePlanBody')}</span>
                  </div>
                ) : (
                  <div className={styles.tableFrame}>
                    <Table className={styles.table}>
                      <TableHeader>
                        <TableRow>
                          <TableHead style={{ width: 130 }}>{t('guidedPortfolios.colIsin')}</TableHead>
                          <TableHead style={{ width: 90 }}>{t('guidedPortfolios.colType')}</TableHead>
                          <TableHead>{t('guidedPortfolios.colName')}</TableHead>
                          <TableHead style={{ width: 72 }}>{t('guidedPortfolios.colCurrency')}</TableHead>
                          <TableHead className={styles.num} style={{ width: 128 }}>
                            {t('guidedPortfolios.colWeight')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {detail.instruments.map((i) => (
                          <TableRow key={i.isin}>
                            <TableCell>{i.isin}</TableCell>
                            <TableCell>{i.tipo_strumento}</TableCell>
                            <TableCell className={styles.ellipsis} title={i.nome_strumento ?? undefined}>
                              {i.nome_strumento ?? i.isin}
                            </TableCell>
                            <TableCell>{i.valuta}</TableCell>
                            <TableCell className={styles.num}>{formatItalianNumber(i.peso * 100)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell colSpan={4}>{t('guidedPortfolios.total')}</TableCell>
                          <TableCell className={styles.num}>
                            {formatItalianNumber(detail.instruments.reduce((sum, i) => sum + i.peso, 0) * 100)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </div>
                )}

                <div className={styles.footer}>
                  <FinecoButton copy={t('guidedPortfolios.cta')} disabled={!canImport} click={importSelection} />
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
